import { useEffect, useState } from "react";
import { Truck, Pencil, Trash2, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Card, BackHeader } from "@/components/common";

const EMPTY = { name: "", vehicle_type: "Frigorifero", plate: "", tractor_plate: "", trailer_plate: "" };

export default function FleetPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    apiFetch("/fleet").then(setItems).catch(() => {});
  }, []);

  const save = async () => {
    setFormError("");
    if (!form.name.trim()) {
      setFormError("Inserisci un nome riconoscibile per il mezzo.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, plate: form.tractor_plate, tractor_plate: form.tractor_plate, trailer_plate: form.trailer_plate, characteristics: [] };
      if (editingId) {
        const v = await apiFetch(`/fleet/${editingId}`, { method: "PATCH", body: JSON.stringify(payload) });
        setItems((old) => old.map((it) => (it.id === editingId ? v : it)));
      } else {
        const v = await apiFetch("/fleet", { method: "POST", body: JSON.stringify(payload) });
        setItems((old) => [v, ...old]);
      }
      setForm(EMPTY);
      setEditingId(null);
    } catch (error) {
      setFormError(error.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({ name: it.name, vehicle_type: it.vehicle_type, plate: it.plate || "", tractor_plate: it.tractor_plate || it.plate || "", trailer_plate: it.trailer_plate || "" });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };
  const remove = async (it) => {
    if (!window.confirm(`Vuoi eliminare ${it.name}? L'operazione non è reversibile.`)) return;
    try {
      await apiFetch(`/fleet/${it.id}`, { method: "DELETE" });
      setItems((old) => old.filter((e) => e.id !== it.id));
      if (editingId === it.id) cancelEdit();
    } catch (error) {
      setFormError(error.message || "Riprova.");
    }
  };

  const inputCls = "min-h-[50px] w-full rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary";
  const label = "mt-2 block text-xs font-bold text-card-foreground";

  return (
    <div className="space-y-3">
      <BackHeader />
      <PageTitle>Gestisci flotta</PageTitle>
      <PageSubtitle>Tieni a portata di mano i mezzi che utilizzi.</PageSubtitle>

      {items.map((it) => (
        <Card key={it.id} className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
              <Truck className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-extrabold text-foreground">{it.name}</p>
              <p className="text-[13px] text-muted-foreground">
                {it.vehicle_type} · Trattore {it.tractor_plate || it.plate || "—"}
                {it.trailer_plate ? ` · Semirimorchio ${it.trailer_plate}` : ""}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid={`fleet-edit-${it.id}`} onClick={() => startEdit(it)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-primary py-2.5 text-xs font-extrabold text-primary">
              <Pencil className="h-4 w-4" /> Modifica
            </button>
            <button data-testid={`fleet-delete-${it.id}`} onClick={() => remove(it)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-destructive py-2.5 text-xs font-extrabold text-destructive">
              <Trash2 className="h-4 w-4" /> Elimina
            </button>
          </div>
        </Card>
      ))}

      <Card className="space-y-1">
        <p className="mb-1 text-lg font-extrabold text-foreground">{editingId ? "Modifica mezzo" : "Aggiungi mezzo"}</p>
        <label className={label}>Nome mezzo</label>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Bilico 01" className={inputCls} />
        <label className={label}>Tipologia</label>
        <input value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} placeholder="Frigorifero" className={inputCls} />
        <label className={label}>Targa trattore</label>
        <input value={form.tractor_plate} onChange={(e) => setForm({ ...form, tractor_plate: e.target.value })} placeholder="AB123CD" className={inputCls} />
        <label className={label}>Targa semirimorchio</label>
        <input value={form.trailer_plate} onChange={(e) => setForm({ ...form, trailer_plate: e.target.value })} placeholder="EF456GH" className={inputCls} />
        {formError ? <p className="mt-1 text-sm text-destructive">{formError}</p> : null}
        <button onClick={save} disabled={saving} className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
          {saving ? "Salvataggio..." : editingId ? "SALVA MODIFICHE" : "AGGIUNGI MEZZO"}
        </button>
        {editingId ? (
          <button onClick={cancelEdit} className="mt-1 w-full py-2 text-sm font-extrabold text-muted-foreground">
            Annulla modifica
          </button>
        ) : null}
      </Card>
    </div>
  );
}
